/* assets/bundle-builder.js
   Vanilla JS custom element for the Bundle Builder section.
   Follows the same pattern as other Broadcast custom elements (collection-component, grid-swatch, etc).
   No external dependencies.
*/

(function () {
  if (customElements.get('bundle-builder-component')) return;

  class BundleBuilderComponent extends HTMLElement {
    constructor() {
      super();
      this.limit = parseInt(this.dataset.selectionLimit, 10) || 3;
      this.bundlePriceCents = parseInt(this.dataset.bundlePrice, 10) || 0;
      this.selectLabel = this.dataset.selectLabel || 'Select';
      this.addedLabel = this.dataset.addedLabel || 'Added';
      this.bundleName = this.dataset.bundleName || 'Bundle';
      this.discountCode = this.dataset.discountCode || '';
      this.selected = new Map();
    }

    connectedCallback() {
      this.grid = this.querySelector('[data-bundle-grid]');
      this.bar = this.querySelector('[data-bundle-bar]');
      this.countEl = this.querySelector('[data-bundle-count]');
      this.thumbsEl = this.querySelector('[data-bundle-thumbs]');
      this.comparePriceEl = this.querySelector('[data-bundle-compare-price]');
      this.submitBtn = this.querySelector('[data-bundle-submit]');
      this.submitLabel = this.querySelector('[data-bundle-submit-label]');
      this.submitLoader = this.querySelector('[data-bundle-submit-loader]');
      this.loadMoreTrigger = this.querySelector('[data-bundle-load-more]');
      this.sectionId = this.dataset.sectionId;
      this.isLoadingNextPage = false;

      if (!this.grid) return;

      this.grid.addEventListener('click', this.onGridClick.bind(this));

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
      }

      this.initPagination();
      this.updateUI();
    }

    initPagination() {
      if (!this.loadMoreTrigger) return;

      const type = this.loadMoreTrigger.dataset.paginationType;

      if (type === 'load_more') {
        const button = this.loadMoreTrigger.querySelector('[data-bundle-load-more-button]');
        button?.addEventListener('click', () => this.loadNextPage(button));
      } else if (type === 'infinite_scroll') {
        this.observeLoadMoreTrigger();
      }
    }

    observeLoadMoreTrigger() {
      if (!this.loadMoreTrigger || !('IntersectionObserver' in window)) return;

      this.loadMoreObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoadingNextPage) {
            this.loadNextPage(null);
          }
        });
      });

      this.loadMoreObserver.observe(this.loadMoreTrigger);
    }

    async loadNextPage(button) {
      if (this.isLoadingNextPage || !this.loadMoreTrigger) return;

      const nextUrl = this.loadMoreTrigger.dataset.nextUrl;
      if (!nextUrl) return;

      this.isLoadingNextPage = true;
      this.loadMoreTrigger.classList.add('is-loading');
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
      }

      try {
        const separator = nextUrl.indexOf('?') === -1 ? '?' : '&';
        const url = nextUrl + separator + 'section_id=' + encodeURIComponent(this.sectionId);

        const response = await fetch(url);
        if (!response.ok) throw new Error('Unable to load more products');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-bundle-grid]');
        const newTrigger = doc.querySelector('[data-bundle-load-more]');

        if (newGrid) {
          // Move (not clone) each new card into the live grid. Click handling
          // is delegated on the grid container, so these work immediately
          // with no extra event binding.
          Array.from(newGrid.children).forEach((child) => {
            this.grid.appendChild(child);
          });
        }

        if (newTrigger && newTrigger.dataset.nextUrl) {
          this.loadMoreTrigger.dataset.nextUrl = newTrigger.dataset.nextUrl;
        } else {
          this.loadMoreObserver?.disconnect();
          this.loadMoreTrigger.remove();
          this.loadMoreTrigger = null;
        }

        // Re-apply selected/disabled state to the freshly appended cards.
        this.updateUI();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoadingNextPage = false;
        this.loadMoreTrigger?.classList.remove('is-loading');
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      }
    }

    onGridClick(event) {
      const selectBtn = event.target.closest('[data-bundle-select]');
      const removeBtn = event.target.closest('[data-bundle-remove]');

      if (selectBtn) {
        if (selectBtn.disabled) return;
        const item = selectBtn.closest('[data-bundle-item]');
        this.toggleSelect(item);
      } else if (removeBtn) {
        const item = removeBtn.closest('[data-bundle-item]');
        this.deselect(item);
      }
    }

    toggleSelect(item) {
      const productId = item.dataset.productId;

      if (this.selected.has(productId)) {
        this.deselect(item);
        return;
      }

      if (this.selected.size >= this.limit) {
        this.flashLimitWarning();
        return;
      }

      this.selected.set(productId, {
        variantId: item.dataset.variantId,
        title: item.dataset.productTitle,
        image: item.dataset.productImage,
        price: parseInt(item.dataset.productPrice, 10) || 0,
        compareAtPrice: parseInt(item.dataset.productCompareAtPrice, 10) || 0,
      });

      this.updateUI();
    }

    deselect(item) {
      const productId = item.dataset.productId;
      this.selected.delete(productId);
      this.updateUI();
    }

    flashLimitWarning() {
      if (!this.bar) return;
      this.bar.classList.add('bundle-builder__bar--shake');
      window.setTimeout(() => {
        this.bar.classList.remove('bundle-builder__bar--shake');
      }, 400);
    }

    updateUI() {
      const count = this.selected.size;

      this.grid.querySelectorAll('[data-bundle-item]').forEach((item) => {
        const isSelected = this.selected.has(item.dataset.productId);
        const isAvailable = item.dataset.available !== 'false';
        const label = item.querySelector('[data-bundle-select-label]');
        const btn = item.querySelector('[data-bundle-select]');

        item.classList.toggle('is-selected', isSelected);

        if (label && isAvailable) {
          label.textContent = isSelected ? this.addedLabel : this.selectLabel;
        }

        if (btn && isAvailable) {
          const atLimit = !isSelected && count >= this.limit;
          btn.disabled = atLimit;
          btn.classList.toggle('is-disabled', atLimit);
        }
      });

      if (this.countEl) this.countEl.textContent = count;

      if (this.thumbsEl) {
        this.thumbsEl.innerHTML = '';
        this.selected.forEach((data) => {
          const thumb = document.createElement('span');
          thumb.className = 'bundle-builder__bar__thumb';
          thumb.setAttribute('role', 'img');
          thumb.setAttribute('aria-label', data.title || '');
          if (data.image) thumb.style.backgroundImage = 'url(' + data.image + ')';
          this.thumbsEl.appendChild(thumb);
        });
      }

      if (this.comparePriceEl) {
        let sum = 0;
        this.selected.forEach((data) => {
          sum += data.compareAtPrice > data.price ? data.compareAtPrice : data.price;
        });
        this.comparePriceEl.textContent = count > 0 && sum > this.bundlePriceCents
          ? this.formatMoney(sum)
          : '';
      }

      const ready = count === this.limit;
      if (this.submitBtn) {
        this.submitBtn.disabled = !ready;
        this.submitBtn.classList.toggle('is-disabled', !ready);
      }

      this.dispatchEvent(
        new CustomEvent('bundle:selection-change', {
          bubbles: true,
          detail: { count, limit: this.limit },
        })
      );
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
      return '₹' + (cents / 100).toFixed(2);
    }

    onSubmit() {
      if (this.selected.size !== this.limit || this.submitBtn.disabled) return;

      const items = Array.from(this.selected.values()).map((data) => ({
        id: parseInt(data.variantId, 10),
        quantity: 1,
        properties: {
          _bundle: this.bundleName,
        },
      }));

      // Hand off to the theme's own <cart-items> element instead of doing our
      // own fetch. It already knows whether cartType is "drawer" or "page",
      // manages the button loading/added state, handles error rendering, and
      // -- for "drawer" installs -- dispatches theme:product:added, which
      // <cart-drawer> already listens for to auto-open. This is what fixes
      // the "redirects to /cart instead of opening the drawer" issue: our
      // previous code redirected unconditionally instead of deferring to
      // this existing logic.
      const cartItems = document.querySelector('cart-items');

      if (!cartItems || typeof cartItems.addToCart !== 'function') {
        // Extremely defensive fallback in case cart-items isn't present yet.
        this.fallbackAddToCart(items);
        return;
      }

      if (this.discountCode) {
        const onAdded = () => {
          document.removeEventListener('theme:product:added', onAdded);
          if (typeof cartItems.applyDiscount === 'function') {
            cartItems.applyDiscount(this.discountCode);
          }
        };
        document.addEventListener('theme:product:added', onAdded, { once: true });
      }

      cartItems.addToCart(items, this.submitBtn);
    }

    // Only used if <cart-items> hasn't been added to the page yet for some reason.
    async fallbackAddToCart(items) {
      this.submitBtn.disabled = true;
      this.submitBtn.classList.add('is-loading');

      try {
        const routes = (window.theme && window.theme.routes) || {};
        const addUrl = routes.cart_add_url || '/cart/add.js';

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Unable to add bundle to cart');
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.submitBtn.disabled = false;
        this.submitBtn.classList.remove('is-loading');
      }
    }

    showError(message) {
      const container = this.querySelector('[data-cart-errors-container]');
      if (!container) return;
      container.innerHTML = '<div class="errors">' + message + '</div>';
      container.classList.add('is-visible');
    }
  }

  customElements.define('bundle-builder-component', BundleBuilderComponent);
})();